<template>
    <div class="relative">
        <editor-content
            v-if="editor && editor.view"
            id="editor"
            :editor="editor"
            class="px-4"
            :class="{
                'pr-8': limit,
                'min-h-60 cursor-text': extendedFocusArea
            }"
            @click.native="extendedFocusArea && editor.chain().focus().run()"
        />
      <div class="bg-gray-50 px-4 py-2 w-full flex items-center justify-between">
        <div v-if="editor" class="flex flex-wrap items-center gap-x-1 gap-y-2">
          <transition name="fade">
            <div v-if="limit"
                 class="text-xs text-gray-500 absolute right-2 top-3"
            >
              <svg
                  height="20"
                  width="20"
                  viewBox="0 0 20 20"
                  class="character-count__graph text-blue-400"
                  :class="{'character-count': true, 'character-count--warning': editor.storage.characterCount.characters() === limit, 'hidden' : editor.storage.characterCount.characters() === 0}"
              >
                <circle
                    r="10"
                    cx="10"
                    cy="10"
                    fill="#e9ecef"
                />
                <circle
                    r="5"
                    cx="10"
                    cy="10"
                    fill="transparent"
                    stroke="currentColor"
                    stroke-width="10"
                    :stroke-dasharray="`calc(${percentage} * 31.4 / 100) 31.4`"
                    transform="rotate(-90) translate(-20)"
                />
                <circle
                    r="6"
                    cx="10"
                    cy="10"
                    fill="white"
                />
              </svg>
              <span class="hidden">
{{ editor.storage.characterCount.characters() }}/{{ limit }}
            characters
            </span>

            </div>

          </transition>
          <button
              v-if="decorations"
              class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
              :class="{ 'bg-gray-200': editor.isActive('bold') }"
              @click="editor.chain().focus().toggleBold().run()"
          >
            B
          </button>
          <button
              v-if="decorations"
              class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
              :class="{ 'bg-gray-200': editor.isActive('italic') }"
              @click="editor.chain().focus().toggleItalic().run()"
          >
            I
        </button>
          <button
              v-if="decorations"
              class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
              :class="{ 'bg-gray-200': editor.isActive('underline') }"
              @click="editor.chain().focus().toggleUnderline().run()"
          >
            U
          </button>
          <template v-if="textAlign">
            <button
                v-tooltip="'Align left'"
                class="rounded-lg bg-gray-100 px-1 py-0.5 text-sm"
                :class="{
                    'bg-gray-200': editor.isActive({
                        textAlign: 'left'
                    })
                }"
                @click="
                    editor.chain().focus().toggleTextAlign('left').run()
                "
            >
                <base-text-left-icon class="size-5" />
            </button>
            <button
                v-tooltip="'Align center'"
                class="rounded-lg bg-gray-100 px-1 py-0.5 text-sm"
                :class="{
                    'bg-gray-200': editor.isActive({
                        textAlign: 'center'
                    })
                }"
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleTextAlign('center')
                        .run()
                "
            >
                <base-text-center-icon class="size-5" />
            </button>
            <button
                v-tooltip="'Align right'"
                class="rounded-lg bg-gray-100 px-1 py-0.5 text-sm"
                :class="{
                    'bg-gray-200': editor.isActive({
                        textAlign: 'right'
                    })
                }"
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleTextAlign('right')
                        .run()
                "
            >
                <base-text-right-icon class="size-5" />
            </button>
            <button
                v-tooltip="'Justify'"
                class="rounded-lg bg-gray-100 px-1 py-0.5 text-sm"
                :class="{
                    'bg-gray-200': editor.isActive({
                        textAlign: 'justify'
                    })
                }"
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleTextAlign('justify')
                        .run()
                "
            >
                <base-text-justify-icon class="size-5" />
            </button>
          </template>
          <button
              v-if="links"
              class="rounded-lg bg-gray-100 px-2 py-0.5"
              @click="setLink"
          >
            <base-link-icon class="w-4 h-4 my-0.5" />
          </button>
          <button
              v-if="attachments"
              v-tooltip="'Attach a file'"
              class="rounded-lg bg-gray-100 px-2 py-0.5"
              @click="$emit('toggle-attachments')"
          >
            <base-paper-clip-icon class="w-4 h-4 my-0.5" />
          </button>
          <button
              v-if="mentions"
              v-tooltip="'Mention user'"
              class="rounded-lg bg-gray-100 px-2 py-0.5"
              @click="insertMention"
          >
            <base-at-symbol-icon class="w-4 h-4 my-0.5" />
          </button>
          <button
              v-if="images"
              v-tooltip="'Embed an image'"
              class="rounded-lg bg-gray-100 px-2 py-0.5"
              @click="addImage"
          >
              <base-image-upload-icon class="w-4 h-4 my-0.5" />
          </button>
          <template v-if="headings">
            <button
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleHeading({ level: 1 })
                        .run()
                "
                class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
                :class="{
                    'bg-gray-200': editor.isActive('heading', {
                        level: 1
                    })
                }"
            >
                H1
            </button>
            <button
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleHeading({ level: 2 })
                        .run()
                "
                class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
                :class="{
                    'bg-gray-200': editor.isActive('heading', {
                        level: 2
                    })
                }"
            >
                H2
            </button>
            <button
                @click="
                    editor
                        .chain()
                        .focus()
                        .toggleHeading({ level: 3 })
                        .run()
                "
                class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
                :class="{
                    'bg-gray-200': editor.isActive('heading', {
                        level: 3
                    })
                }"
            >
                H3
            </button>
          </template>
          <template v-if="textStyles">
            <div
                class="flex max-h-6 cursor-default items-center rounded border pl-1"
                v-tooltip="'Text color'"
            >
                <p class="text-base font-bold">
                    <span class="text-red-500">A</span>
                    <span class="text-green-500">b</span>
                </p>
                <colour-picker
                    thumbnail-only
                    class="translate-x-0.5 scale-[65%] transform"
                    :swatches="swatches"
                    :value="editor.getAttributes('textStyle').color"
                    @input="
                        value =>
                            editor.chain().focus().setColor(value).run()
                    "
                />
            </div>
            <div
                class="flex max-h-6 cursor-default items-center rounded border pl-0.5"
                v-tooltip="'Text background color'"
            >
                <p
                    class="rounded-l bg-yellow-400 px-0.5 text-base font-bold leading-5 text-gray-700"
                >
                    Ab
                </p>
                <colour-picker
                    thumbnail-only
                    id="text-background-color"
                    class="translate-x-0.5 scale-[65%] transform"
                    :swatches="swatches"
                    :value="
                        editor.getAttributes('textStyle')
                            .backgroundColor
                    "
                    @input="
                        value =>
                            editor
                                .chain()
                                .focus()
                                .setBackgroundColor(value)
                                .run()
                    "
                />
            </div>

            <div class="relative inline-block text-left">
                <div>
                    <button
                        @click="showDropdown = !showDropdown"
                        type="button"
                        class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-1 py-px text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-100"
                        id="options-menu"
                        aria-haspopup="true"
                        aria-expanded="true"
                    >
                        Size
                        <base-chevron-icon
                            class="!ml-0 !mr-0 translate-x-0.5 scale-75"
                            :class="{
                                'rotate-180': showDropdown
                            }"
                        />
                    </button>
                </div>

                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <div
                        v-if="showDropdown"
                        v-click-outside="() => (showDropdown = false)"
                        class="absolute right-0 z-50 w-16 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
                    >
                        <button
                            v-for="size in fontSizes"
                            :key="`${size}`"
                            class="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                            :class="{
                                'bg-gray-200': editor.isActive(
                                    'textStyle',
                                    { fontSize: `${size}px` }
                                )
                            }"
                            @click="
                                editor
                                    .chain()
                                    .focus()
                                    .setFontSize(`${size}px`)
                                    .run()
                            "
                        >
                            {{ size }}px
                        </button>
                    </div>
                </transition>
            </div>
            <button
                class="rounded-lg bg-gray-100 px-2 py-0.5 text-sm font-bold"
                @click="editor.chain().focus().unsetFontSize().run()"
            >
                Unset size
            </button>
          </template>
        </div>
        <div>
          <slot />
        </div>
      </div>

        <link-input-modal
            v-if="!forceRootRenderModal"
            :can-add-buttons="buttons"
            @create-link="createLink"
        />
    </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import { CharacterCount, Placeholder } from '@tiptap/extensions';
import { TextStyleKit } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import Mention from '@tiptap/extension-mention';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import FileHandler from '@tiptap-pro/extension-file-handler';
import { mergeAttributes } from '@tiptap/core';
import { VueRenderer } from '@tiptap/vue-2';
import { computePosition, flip, shift, offset } from '@floating-ui/dom';
import MentionList from './MentionList.vue';
import LinkInputModal from '@/components/modals/LinkInputModal';
import ColourPicker from '@/components/storeDesigner/ColourPicker';
import { uploadDirect } from '@uploadcare/upload-client';
import vClickOutside from 'v-click-outside';
import Vue from 'vue';

export default {
    components: {
        ColourPicker,
        EditorContent,
        LinkInputModal
    },

    directives: {
        clickOutside: vClickOutside.directive
    },

    props: {
        value: {
            type: String,
            default: ''
        },
        users: {
            type: Array,
            required: false,
            default: () => []
        },
        attachments: {
            type: Boolean,
            required: false,
            default: false
        },
        decorations: {
            type: Boolean,
            required: false,
            default: false
        },
        mentions: {
            type: Boolean,
            required: false,
            default: false
        },
        links: {
            type: Boolean,
            required: false,
            default: false
        },
        images: {
            type: Boolean,
            required: false,
            default: false
        },
        textAlign: {
            type: Boolean,
            required: false,
            default: false
        },
        headings: {
            type: Boolean,
            required: false,
            default: false
        },
        textStyles: {
            type: Boolean,
            required: false,
            default: false
        },
        buttons: {
            type: Boolean,
            required: false,
            default: false
        },
        extendedFocusArea: {
            type: Boolean,
            required: false,
            default: false
        },
        swatches: {
            type: Array,
            required: false,
            default: () => [
                '#000000',
                '#222831',
                '#ffffff',
                '#393e46',
                '#583d72',
                '#9f5f80',
                '#99c1de',
                '#84a59d',
                '#ffd369',
                '#f5cac3',
                '#ff8e71'
            ]
        },
        headingFont: {
            type: [Object, null],
            required: false,
            default: null
        },
        bodyFont: {
            type: [Object, null],
            required: false,
            default: null
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Leave a comment…'
        },
        forceRootRenderModal: {
            type: Boolean,
            required: false,
            default: false
        }
    },

    data() {
        return {
            editor: null,
            limit: 240,
            linkInputModal: null,
            showDropdown: false,
            fontSizes: [8, 9, 10, 11, 12, 14, 18, 24, 30, 36, 48, 60, 72, 96]
        };
    },

    watch: {
        value(value) {
            if (!this.editor || !this.editor.view) {
                return;
            }

            const isSame = this.editor.getHTML() === value;

            if (isSame) {
                return;
            }

            this.editor.commands.setContent(value, false);
        },
        watch: {
            headingFont(val) {
                this.loadCustomFont(val);

                this.applyEditorFonts({
                    bodyFont: this.bodyFont?.name || null,
                    headingFont: val?.name || null
                });
            },
            bodyFont(val) {
                this.loadCustomFont(val);

                this.applyEditorFonts({
                    bodyFont: val?.name || null,
                    headingFont: this.headingFont?.name || null
                });
            }
        }
    },

    async created() {
        await Promise.all([
            this.loadCustomFont(this.bodyFont),
            this.loadCustomFont(this.headingFont)
        ]);

        this.applyEditorFonts({
            bodyFont: this.bodyFont?.name || null,
            headingFont: this.headingFont?.name || null
        });
    },

    mounted() {
        if (this.mentions) {
            this.$emit('get-users');
        }

        const CustomMention = Mention.extend({
            renderHTML({ node, HTMLAttributes }) {
                return [
                    'span',
                    mergeAttributes(
                        this.options.HTMLAttributes,
                        HTMLAttributes
                    ),
                    `@${node.attrs.name}`
                ];
            },
            addAttributes() {
                return {
                    name: {
                        default: null,
                        parseHTML: element => {
                            return {
                                name: element.getAttribute('data-mention-name')
                            };
                        },
                        renderHTML: attributes => {
                            if (!attributes.name) {
                                return {};
                            }
                            return {
                                'data-mention-name': attributes.name
                            };
                        }
                    },
                    id: {
                        default: null,
                        parseHTML: element => {
                            return {
                                id: element.getAttribute('data-mention-id')
                            };
                        },
                        renderHTML: attributes => {
                            if (!attributes.id) {
                                return {};
                            }

                            return {
                                'data-mention-id': attributes.id
                            };
                        }
                    }
                };
            }
        });

        const extensions = [
            StarterKit.configure({ heading: false }),
            Placeholder.configure({
                placeholder: this.placeholder
            }),
            CharacterCount.configure({
                limit: this.limit
            })
        ];

        if (this.textAlign) {
            extensions.push(
                TextAlign.configure({
                    types: ['heading', 'paragraph']
                })
            );
        }

        if (this.textStyles) {
            extensions.push(TextStyleKit);
        }

        if (this.headings) {
            const CustomHeading = Heading.extend({
                addAttributes() {
                    return {
                        ...this.parent?.(),
                        class: {
                            default: null,
                            renderHTML: attributes => {
                                if (!attributes.class) return {};
                                return { class: attributes.class };
                            }
                        }
                    };
                },

                renderHTML({ node, HTMLAttributes }) {
                    const level = node.attrs.level;

                    const classMap = {
                        1: 'text-3xl font-heading',
                        2: 'text-2xl font-heading',
                        3: 'text-xl font-heading'
                    };

                    return [
                        `h${level}`,
                        {
                            ...HTMLAttributes,
                            class: classMap[level]
                        },
                        0
                    ];
                }
            });

            extensions.push(CustomHeading);
        }

        if (this.links) {
            extensions.push(
                Link.extend({ inclusive: false }).configure({
                    openOnClick: false
                })
            );
        }

        if (this.mentions) {
            extensions.push(
                CustomMention.configure({
                    HTMLAttributes: {
                        class: 'mention'
                    },
                    suggestion: {
                        items: ({ query }) => {
                            return this.users
                                .filter(item =>
                                    item.name
                                        .toLowerCase()
                                        .startsWith(query.toLowerCase())
                                )
                                .slice(0, 5);
                        },
                        render: () => {
                            let component;
                            let popup;

                            return {
                                onStart: props => {
                                    component = new VueRenderer(MentionList, {
                                        parent: this,
                                        propsData: props
                                    });

                                    if (!props.clientRect) {
                                        return;
                                    }

                                    // Create virtual element for Floating UI
                                    const virtualElement = {
                                        getBoundingClientRect: props.clientRect
                                    };

                                    popup = document.createElement('div');
                                    popup.style.position = 'absolute';
                                    popup.style.top = '0';
                                    popup.style.left = '0';
                                    popup.style.zIndex = '1000';
                                    popup.appendChild(component.element);
                                    document.body.appendChild(popup);

                                    computePosition(virtualElement, popup, {
                                        placement: 'bottom-start',
                                        middleware: [
                                            offset(8),
                                            flip(),
                                            shift({ padding: 8 })
                                        ]
                                    }).then(({ x, y }) => {
                                        Object.assign(popup.style, {
                                            left: `${x}px`,
                                            top: `${y}px`
                                        });
                                    });
                                },
                                onUpdate(props) {
                                    component.updateProps(props);

                                    if (!props.clientRect || !popup) {
                                        return;
                                    }

                                    const virtualElement = {
                                        getBoundingClientRect: props.clientRect
                                    };

                                    computePosition(virtualElement, popup, {
                                        placement: 'bottom-start',
                                        middleware: [
                                            offset(8),
                                            flip(),
                                            shift({ padding: 8 })
                                        ]
                                    }).then(({ x, y }) => {
                                        Object.assign(popup.style, {
                                            left: `${x}px`,
                                            top: `${y}px`
                                        });
                                    });
                                },
                                onKeyDown(props) {
                                    if (props.event.key === 'Escape') {
                                        if (popup && popup.parentNode) {
                                            popup.parentNode.removeChild(popup);
                                        }
                                        return true;
                                    }

                                    return component.ref?.onKeyDown(props);
                                },
                                onExit() {
                                    if (popup && popup.parentNode) {
                                        popup.parentNode.removeChild(popup);
                                    }
                                    component.destroy();
                                }
                            };
                        }
                    }
                })
            );
        }

        if (this.images) {
            extensions.push(
                ...[
                    Image,
                    FileHandler.configure({
                        allowedMimeTypes: [
                            'image/png',
                            'image/jpeg',
                            'image/gif',
                            'image/webp'
                        ],
                        onDrop: (currentEditor, files, pos) =>
                            this.handleImageStorage(currentEditor, files, pos),
                        onPaste: (currentEditor, files) =>
                            this.handleImageStorage(
                                currentEditor,
                                files,
                                currentEditor.state.selection.anchor
                            )
                    })
                ]
            );
        }

        this.editor = new Editor({
            content: this.value,
            extensions,
            editorProps: {
                attributes: {
                    class: 'prose prose-sm sm:prose-base m-1 focus:outline-none'
                }
            },

            onUpdate: () => {
                if (this.editor.storage.characterCount.characters() === 0) {
                    this.$emit('input', '');
                } else {
                    this.$emit('input', this.editor.getHTML());
                }
            },

            onFocus: () => {
                this.$emit('focus');
            }
        });
    },

    beforeDestroy() {
        if (this.linkInputModal) {
            this.linkInputModal.$el.remove();
            this.linkInputModal = null;
        }

        const editor = this.editor;
        this.editor = null;
        
        this.$nextTick(() => {
            if (editor) {
                editor.destroy();
            }
        });
    },

    computed: {
        percentage() {
            if (!this.editor || !this.editor.view) {
                return 0;
            }
            return Math.round(
                (100 / this.limit) *
                    this.editor.storage.characterCount.characters()
            );
        }
    },

    methods: {
        setLink() {
            if (!this.editor || !this.editor.view) {
                return;
            }
            
            const previousUrl = this.editor.getAttributes('link').href;

            if (this.forceRootRenderModal && !this.linkInputModal) {
                const Modal = Vue.extend(LinkInputModal);
                this.linkInputModal = new Modal({
                    propsData: {
                        canAddButtons: this.buttons
                    }
                });

                this.linkInputModal.$on('create-link', this.createLink);
                this.linkInputModal.$on('closed', () => {
                    this.linkInputModal?.$el.remove();
                    this.linkInputModal = null;
                });

                this.linkInputModal.$mount();
                const container = document.getElementById('modals-container');

                if (!container) {
                    return;
                }

                container.appendChild(this.linkInputModal.$el);
            }

            this.$modal.show('link-input-modal', {
                previousUrl
            });
        },

        async createLink({ text, link, isButtonLike = false }) {
            if (!this.editor || !this.editor.view) {
                return;
            }

            if (link === '') {
                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .unsetLink()
                    .run();

                return;
            }

            if (link) {
                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .deleteSelection()
                    .run(); //workaround for editing links with custom text

                this.editor
                    .chain()
                    .focus()
                    .extendMarkRange('link')
                    .setLink({
                        href: link,
                        target: '_blank',
                        class: isButtonLike
                            ? 'inline-block clip-path-button border-button-primary bg-button rounded-button rounded-md font-button text-button px-12 py-4'
                            : 'comment-link'
                    })
                    .command(({ tr }) => {
                        tr.insertText(text);
                        return true;
                    })
                    .run();
            }
        },
        insertMention() {
            if (!this.editor || !this.editor.view) {
                return;
            }

            this.editor
                .chain()
                .focus()
                .command(({ tr }) => {
                    tr.insertText(`@`);
                    return true;
                })
                .run();
        },

        async addImage() {
            if (!this.editor || !this.editor.view) {
                return;
            }

            const pickerOpts = {
                types: [
                    {
                        description: 'Images',
                        accept: {
                            'image/*': [
                                '.jpg',
                                '.jpeg',
                                '.png',
                                '.gif',
                                '.webp'
                            ]
                        }
                    }
                ],
                excludeAcceptAllOption: true,
                multiple: false
            };

            try {
                const [fileHandle] = await window.showOpenFilePicker(
                    pickerOpts
                );

                const file = await fileHandle.getFile();

                this.handleImageStorage(
                    this.editor,
                    [file],
                    this.editor.state.selection.anchor
                );
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        },

        async handleImageStorage(currentEditor, files, pos) {
            if (files && files.length) {
                const clientOptions = {
                    publicKey: '71f65abd77a8d66ef2e1',
                    store: 'auto'
                };

                const promises = files.map(file =>
                    uploadDirect(file, clientOptions)
                );

                try {
                    const storedFiles = await Promise.all(promises);

                    storedFiles.forEach(file => {
                        currentEditor
                            .chain()
                            .insertContentAt(pos, {
                                type: 'image',
                                attrs: { src: file.cdnUrl }
                            })
                            .focus()
                            .run();
                    });
                } catch (error) {
                    throw new Error(`API ${error}`);
                }
            }
        },

        async loadCustomFont(val) {
            if (!val) {
                return;
            }

            const font = new FontFace(
                val.name,
                `url(${(val?.variants?.length && val?.variants[0]?.url) || ''})`
            );

            try {
                await font.load();

                document.fonts.add(font);
            } catch (err) {
                window.alert(err);
            }
        },

        applyEditorFonts({ bodyFont = null, headingFont = null }) {
            const prevStyle = document.getElementById('editor-font-style');

            if (prevStyle) {
                prevStyle.remove();
            }

            const style = document.createElement('style');
            style.id = 'editor-font-style';
            const bodyFontTextContent = bodyFont
                ? `
                #editor {
                    font-family: "${bodyFont}", sans-serif;
                }`
                : '';

            const headingFontTextContent = headingFont
                ? `
                #editor h1,
                #editor h2,
                #editor h3,
                #editor h4,
                #editor h5,
                #editor h6 {
                    font-family: "${headingFont}", sans-serif;
                }`
                : '';

            style.textContent = `
                ${bodyFontTextContent}
                ${headingFontTextContent}
            `;

            document.head.appendChild(style);
        },
    }
};
</script>

<style src="@/assets/css/animations.css" />

<style>
.tiptap p.is-editor-empty:first-child::before {
    color: #adb5bd;
    content: attr(data-placeholder);
    float: left;
    height: 0;
    pointer-events: none;
}

.character-count {
    display: flex;
    align-items: center;
    color: #68cef8;
}
.character-count--warning {
    color: #fb5151;
}
.character-count__graph {
    margin-right: 0.5rem;
}
.character-count__text {
    color: #868e96;
}
</style>
